import { omitProperties } from "@utils/Utils";
import { EDocumentImportDefinition, ResolvedEDocument } from "./types";

/**
 * Un comprobante listo para que la lambda lo escriba.
 *
 * `key` viaja aparte del header porque la lambda la usa para derivar el id de forma
 * deterministica (idempotencia) y para señalar cual documento fallo dentro del lote.
 */
export type EDocumentImportRow = {
    key: string;
    document: Record<string, unknown>;
    lines: Record<string, unknown>[];
    payments: Record<string, unknown>[];
};

/** Atributos de la linea que solo existen en el navegador y no se persisten. */
const LINE_TRANSIENT_ATTRIBUTES = [
    "id",
    "product",
    "forceRecalculatePrice",
    "preSavedId",
    "toDelete",
    "selected",
] as const;

const buildLine = (document: ResolvedEDocument): Record<string, unknown>[] =>
    document.lines.map((line) => {
        const data = omitProperties({ ...line.data }, [...LINE_TRANSIENT_ATTRIBUTES], true) as Record<
            string,
            unknown
        >;

        if (data.exemption && typeof data.exemption === "object") {
            data.exemption = JSON.stringify(data.exemption);
        }
        // Sin coincidencia en inventario la linea va sin `productId`: es un producto temporal,
        // igual que cuando se agrega uno a mano desde el offcanvas de productos.
        if (line.productId) data.productId = line.productId;

        return data;
    });

/**
 * Arma el payload que se manda a la lambda, delegando en la definicion todo lo que es
 * especifico del tipo de comprobante (header y pagos).
 */
export const buildEDocumentPayload = (
    documents: ResolvedEDocument[],
    definition: EDocumentImportDefinition
): EDocumentImportRow[] =>
    documents
        .filter((document) => document.action === "create")
        .map((document) => {
            const refs = {
                clientId: document.clientId,
                currencyId: document.currencyId,
                exchangeRate: document.exchangeRate,
                referencedDocument: document.referencedDocument,
            };

            return {
                key: document.parsed.clave,
                document: definition.buildHeader(document.parsed, refs),
                lines: buildLine(document),
                payments: definition.buildPayments(document.parsed, refs),
            };
        });
